import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { useAuth } from '../lib/auth'
import { formatDateTime } from '../lib/format'
import type { Customer, DeviceTemplate, Richtlinie, RichtlinienUebersicht } from '../lib/types'
import { EmptyState, ErrorBox, Loading } from './Feedback'

const SCHWERE: Record<Richtlinie['schwere'], { text: string; klasse: string }> = {
  info: { text: 'Info', klasse: 'badge badge-off' },
  warn: { text: 'Warnung', klasse: 'badge badge-warn' },
  crit: { text: 'Kritisch', klasse: 'badge badge-crit' },
}

/** Die Regel in einem Satz, wie sie in der Liste steht. */
export function regelText(r: Pick<Richtlinie, 'art' | 'muster'>): string {
  return r.art === 'muss' ? `muss enthalten: ${r.muster}` : `darf nicht enthalten: ${r.muster}`
}

/** Das Ergebnis einer Regel über alle geprüften Geräte. */
export function regelErgebnis(r: Pick<Richtlinie, 'geprueft' | 'verletzt'>): string {
  if (r.geprueft === 0) return 'noch nicht geprüft'
  if (r.verletzt === 0) return `alle ${r.geprueft} erfüllt`
  return `${r.verletzt} von ${r.geprueft} nicht erfüllt`
}

/**
 * Richtlinien für Konfigurationen (Stufe 5.2): Regeln wie „Syslog muss an
 * sein“ werden bei jeder Sicherung geprüft. Am Gerät wird nichts geändert;
 * ein Verstoß ist ein Hinweis und ein Alarm.
 */
export function Richtlinien({ customer }: { customer?: Customer }) {
  const { me, canForCustomer } = useAuth()
  const queryClient = useQueryClient()
  const global = me?.global_permissions.includes('settings.manage') ?? false
  const darf = customer ? canForCustomer(customer.id, 'settings.manage') : global

  const daten = useQuery({
    queryKey: ['richtlinien', customer?.id ?? null],
    queryFn: ({ signal }) =>
      apiGet<RichtlinienUebersicht>(`/richtlinien${customer ? `?kunde=${customer.id}` : ''}`, signal),
    refetchInterval: 60_000,
  })

  const loeschen = useMutation({
    mutationFn: (id: string) => apiSend<void>('DELETE', `/richtlinien/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['richtlinien'] })
    },
  })

  if (daten.isPending) return <Loading what="Die Richtlinien" />
  if (daten.isError) return <ErrorBox error={daten.error} what="Die Richtlinien" />
  const { regeln, geraete } = daten.data
  const mitVerstoss = geraete.filter((g) => g.pruefungen.some((p) => !p.erfuellt))

  return (
    <>
      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Regeln</h2>
          <span className="muted">Geprüft bei jeder Sicherung · am Gerät wird nichts geändert</span>
        </div>
        {regeln.length === 0 ? (
          <div className="card-body muted">
            Noch keine Regel. Beispiel: „Syslog an“ – muss enthalten <code>set status enable</code>.
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Regel</th>
                <th>Gilt für</th>
                <th>Schwere</th>
                <th>Ergebnis</th>
                {darf || global ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {regeln.map((r) => (
                <tr key={r.id}>
                  <td>
                    <strong>{r.name}</strong>
                    {r.beschreibung ? <div className="muted">{r.beschreibung}</div> : null}
                  </td>
                  <td>
                    <code>{regelText(r)}</code>
                  </td>
                  <td>
                    {r.kunde_id ? r.kunde : 'alle Kunden'}
                    {r.vorlage ? <div className="muted">nur Vorlage {r.vorlage}</div> : null}
                  </td>
                  <td>
                    <span className={SCHWERE[r.schwere].klasse}>{SCHWERE[r.schwere].text}</span>
                  </td>
                  <td>{regelErgebnis(r)}</td>
                  {darf || global ? (
                    <td style={{ textAlign: 'right' }}>
                      {(r.kunde_id ? darf || global : global) ? (
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={loeschen.isPending}
                          onClick={() => loeschen.mutate(r.id)}
                        >
                          Löschen
                        </button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {loeschen.isError ? (
          <div className="card-body" style={{ color: 'var(--crit)' }}>
            {(loeschen.error as Error).message}
          </div>
        ) : null}
        {darf || global ? <RegelFormular customer={customer} global={global} darfKunde={darf} /> : null}
      </section>

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Ergebnis je Gerät</h2>
          <span className="muted">
            {geraete.length} geprüft, {mitVerstoss.length} mit Verstoß
          </span>
        </div>
        {geraete.length === 0 ? (
          <EmptyState
            title="Noch nichts geprüft"
            hint="Geprüft werden Geräte mit mindestens einer Config-Sicherung, sobald eine Regel für sie gilt."
          />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Gerät</th>
                {customer ? null : <th>Kunde</th>}
                <th>Nicht erfüllt</th>
                <th>Erfüllt</th>
                <th>Geprüft</th>
              </tr>
            </thead>
            <tbody>
              {geraete.map((g) => {
                const offen = g.pruefungen.filter((p) => !p.erfuellt)
                const ok = g.pruefungen.length - offen.length
                return (
                  <tr key={g.geraet_id}>
                    <td>{g.geraet}</td>
                    {customer ? null : <td>{g.kunde}</td>}
                    <td>
                      {offen.length === 0 ? (
                        <span className="badge badge-ok">alles erfüllt</span>
                      ) : (
                        <ul className="regel-liste">
                          {offen.map((p) => (
                            <li key={p.regel_id}>
                              <span className={SCHWERE[p.schwere].klasse}>{p.regel}</span>
                              {p.art === 'darf_nicht' && p.zeile ? (
                                <span className="muted"> · Zeile {p.zeile}</span>
                              ) : p.art === 'muss' ? (
                                <span className="muted"> · fehlt</span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td>
                      {ok} von {g.pruefungen.length}
                    </td>
                    <td className="muted">{formatDateTime(g.pruefungen[0]?.geprueft ?? '')}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </section>
    </>
  )
}

function RegelFormular({ customer, global, darfKunde }: { customer?: Customer; global: boolean; darfKunde: boolean }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [art, setArt] = useState<Richtlinie['art']>('muss')
  const [muster, setMuster] = useState('')
  const [schwere, setSchwere] = useState<Richtlinie['schwere']>('warn')
  const [vorlage, setVorlage] = useState('')
  // Mit gewähltem Kunden gilt eine neue Regel für ihn, sonst für alle.
  const [fuerAlle, setFuerAlle] = useState(!customer)

  const vorlagen = useQuery({
    queryKey: ['templates'],
    queryFn: ({ signal }) => apiGet<DeviceTemplate[]>('/templates', signal),
    staleTime: 300_000,
  })

  const anlegen = useMutation({
    mutationFn: () =>
      apiSend<{ regel: Richtlinie; geprueft: number }>('POST', '/richtlinien', {
        kunde_id: fuerAlle || !customer ? null : customer.id,
        name: name.trim(),
        art,
        muster,
        schwere,
        vorlage,
      }),
    onSuccess: () => {
      setName('')
      setMuster('')
      void queryClient.invalidateQueries({ queryKey: ['richtlinien'] })
    },
  })

  const kannAlle = global
  const kannKunde = Boolean(customer) && darfKunde

  return (
    <form
      className="card-body row"
      style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', borderTop: '1px solid var(--border)' }}
      onSubmit={(e) => {
        e.preventDefault()
        anlegen.mutate()
      }}
    >
      <div className="field">
        <label htmlFor="regel-name">Name</label>
        <input id="regel-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="regel-art">Art</label>
        <select
          id="regel-art"
          className="input"
          value={art}
          onChange={(e) => setArt(e.target.value as Richtlinie['art'])}
        >
          <option value="muss">muss enthalten</option>
          <option value="darf_nicht">darf nicht enthalten</option>
        </select>
      </div>
      <div className="field grow">
        <label htmlFor="regel-muster">Muster (regulärer Ausdruck, je Zeile)</label>
        <input
          id="regel-muster"
          className="input mono"
          placeholder="z. B. set allowaccess .*\bhttp\b"
          value={muster}
          onChange={(e) => setMuster(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="regel-vorlage">Vorlage</label>
        <select id="regel-vorlage" className="input" value={vorlage} onChange={(e) => setVorlage(e.target.value)}>
          <option value="">alle Vorlagen</option>
          {(vorlagen.data ?? []).map((t) => (
            <option key={t.key} value={t.key}>
              {t.name}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="regel-schwere">Schwere</label>
        <select
          id="regel-schwere"
          className="input"
          value={schwere}
          onChange={(e) => setSchwere(e.target.value as Richtlinie['schwere'])}
        >
          <option value="info">Info</option>
          <option value="warn">Warnung</option>
          <option value="crit">Kritisch</option>
        </select>
      </div>
      {kannAlle && kannKunde ? (
        <label className="row" style={{ gap: 6 }}>
          <input type="checkbox" checked={fuerAlle} onChange={(e) => setFuerAlle(e.target.checked)} />
          Für alle Kunden
        </label>
      ) : null}
      <button
        type="submit"
        className="btn btn-sm"
        disabled={anlegen.isPending || !name.trim() || !muster.trim() || (!kannKunde && !kannAlle)}
      >
        Regel hinzufügen
      </button>
      {anlegen.isSuccess ? <span className="muted">{anlegen.data.geprueft} Geräte geprüft</span> : null}
      {anlegen.isError ? <span style={{ color: 'var(--crit)' }}>{(anlegen.error as Error).message}</span> : null}
    </form>
  )
}
